import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { netTermsFromDTO } from '~/infra/translator';
import { assertUnreachable, notEmpty } from '~/infra/utils';
import { toCustomPageDTO } from '~/models/custom-page';
import * as serviceModel from '~/models/service';
import { EffectiveDateSource } from '~/models/service/model';
import { fromAmendmentDTO as billingFromAmendmentDTO, toAmendmentDTO as billingToAmendmentDTO } from '~/models/service-billing/amendment';
import { toDTO as billingToDTO } from '~/models/service-billing/translator';
import { fromDTO as costFromDTO, toDTO as costToDTO } from '~/models/service-cost/translator';
import { PracticeManagementServiceIntegrationModel } from '~/models/ServiceIntegrations';
import { toDateStampObject } from '~/models/time/translator';
import { PendingAmendmentModel } from '../PendingAmendmentModel';
import { AmendmentDraftChangeType, makeServiceAddition, makeServiceBillingTriggerUpdate, lastModificationFromDTO } from './amendment';
export function fromDTO(dto) {
    var _dto_inProgressAmendments;
    return _object_spread_props(_object_spread({}, dto), {
        lastModification: lastModificationFromDTO(dto.lastModification),
        legalTermsDocuments: dto.legalTermsDocuments || [],
        serviceBundles: dto.serviceBundles ? {
            bundles: dto.serviceBundles.bundles.map((b)=>_object_spread_props(_object_spread({}, b), {
                    services: b.services.map(serviceModel.fromDTO).filter(notEmpty)
                }))
        } : undefined,
        services: dto.services.map(serviceModel.fromDTO).filter(notEmpty),
        pendingAmendments: dto.pendingAmendments.map(PendingAmendmentModel.fromDTO),
        inProgressAmendments: ((_dto_inProgressAmendments = dto.inProgressAmendments) !== null && _dto_inProgressAmendments !== void 0 ? _dto_inProgressAmendments : []).map(PendingAmendmentModel.fromDTO),
        practiceManagementSyncFailures: dto.practiceManagementSyncFailures ? dto.practiceManagementSyncFailures.map(PracticeManagementServiceIntegrationModel.fromDTO) : undefined,
        amendment: amendmentFromDTO(dto.amendment)
    });
}
export function amendmentFromDTO(amendment) {
    var _amendment_draft_version, _amendment_draft_serviceNotes;
    return amendment ? {
        draft: {
            changes: amendment.draft.changes.map(amendmentDraftChangeFromDTO).filter(notEmpty),
            version: (_amendment_draft_version = amendment.draft.version) !== null && _amendment_draft_version !== void 0 ? _amendment_draft_version : '',
            serviceNotes: (_amendment_draft_serviceNotes = amendment.draft.serviceNotes) !== null && _amendment_draft_serviceNotes !== void 0 ? _amendment_draft_serviceNotes : []
        }
    } : undefined;
}
function draftCostToDTO(cost) {
    return costToDTO({
        cost,
        shouldResetDiscount: false
    });
}
function draftCostFromDTO(cost) {
    return costFromDTO(cost);
}
function addedServiceEffectiveDate(service) {
    if (!serviceModel.isAwaitingApproval(service.status)) return null;
    const { effectiveDate, effectiveDateSource } = service.status.awaitingApproval;
    return effectiveDate != null && effectiveDateSource === EffectiveDateSource.User ? toDateStampObject(effectiveDate) : null;
}
function addServiceRequestToDTO(service) {
    return _object_spread(_object_spread_props(_object_spread({
        id: service.id,
        name: service.name,
        description: service.description,
        terms: service.terms,
        serviceTemplateId: service.serviceTemplateId,
        billedUpon: billingToDTO(service.billing),
        cost: serviceModel.toDTO(service).cost
    }, service.taxable != null && {
        taxable: service.taxable
    }, service.bundle && {
        bundle: {
            services: service.bundle.services.map(serviceModel.includedServiceToDTO)
        }
    }), {
        effectiveDate: addedServiceEffectiveDate(service)
    }), service.integrations.hasIntegrations() && {
        integrations: service.integrations.toJSON()
    });
}
export function amendmentDraftChangeToDTO(change) {
    switch(change.type){
        case AmendmentDraftChangeType.ServiceCostUpdate:
            return {
                type: change.type,
                value: {
                    serviceId: change.value.serviceId,
                    cost: draftCostToDTO(change.value.cost)
                }
            };
        case AmendmentDraftChangeType.NetTermsUpdate:
            return {
                type: change.type,
                value: {
                    netTerms: change.value.netTerms
                }
            };
        case AmendmentDraftChangeType.ServiceBillingTriggerUpdate:
            return {
                type: change.type,
                value: {
                    serviceId: change.value.serviceId,
                    billedUpon: billingToAmendmentDTO(change.value.billedUpon)
                }
            };
        case AmendmentDraftChangeType.ServiceBundleUpdate:
            return {
                type: change.type,
                value: {
                    serviceId: change.value.serviceId,
                    bundle: {
                        services: change.value.bundle.services.map(serviceModel.includedServiceToDTO)
                    }
                }
            };
        case AmendmentDraftChangeType.ServiceAddition:
            return {
                type: change.type,
                value: {
                    serviceId: change.value.serviceId,
                    addServiceRequest: addServiceRequestToDTO(change.value.service)
                }
            };
        case AmendmentDraftChangeType.ServiceBundleRemoval:
        case AmendmentDraftChangeType.ServiceNameUpdate:
        case AmendmentDraftChangeType.ServiceDescriptionUpdate:
        case AmendmentDraftChangeType.ServiceTermsUpdate:
        case AmendmentDraftChangeType.ServiceTermsRemoval:
        case AmendmentDraftChangeType.ServiceTermination:
        case AmendmentDraftChangeType.ServiceCancellation:
        case AmendmentDraftChangeType.ServiceIntegratedClassUpdate:
        case AmendmentDraftChangeType.ServiceTaxabilityUpdate:
        case AmendmentDraftChangeType.PaymentApprovalRequirementUpdate:
        case AmendmentDraftChangeType.TaxSettingsUpdate:
        case AmendmentDraftChangeType.CreditCardFeeCoverageUpdate:
        case AmendmentDraftChangeType.LegalTermsDocumentsUpdate:
        case AmendmentDraftChangeType.AmendmentsApprovalMethodUpdate:
        case AmendmentDraftChangeType.InvoicesApprovalMethodUpdate:
            return change;
        case AmendmentDraftChangeType.CustomPageAddition:
        case AmendmentDraftChangeType.CustomPageUpdate:
            return {
                type: change.type,
                value: {
                    page: toCustomPageDTO(change.value.page)
                }
            };
        default:
            return assertUnreachable(change);
    }
}
function dropUnknownDraftChange(_exhaustivenessCheck) {
    return undefined;
}
export function amendmentDraftChangeFromDTO(dto) {
    switch(dto.type){
        case AmendmentDraftChangeType.ServiceCostUpdate:
            return {
                type: dto.type,
                value: {
                    serviceId: dto.value.serviceId,
                    cost: draftCostFromDTO(dto.value.cost),
                    conflicts: dto.value.conflicts
                }
            };
        case AmendmentDraftChangeType.NetTermsUpdate:
            return {
                type: dto.type,
                value: {
                    netTerms: netTermsFromDTO(dto.value.netTerms)
                }
            };
        case AmendmentDraftChangeType.ServiceBillingTriggerUpdate:
            return makeServiceBillingTriggerUpdate(dto.value.serviceId, billingFromAmendmentDTO(dto.value.billedUpon));
        case AmendmentDraftChangeType.ServiceBundleUpdate:
            return {
                type: dto.type,
                value: {
                    serviceId: dto.value.serviceId,
                    bundle: {
                        services: dto.value.bundle.services.map(serviceModel.includedServiceFromDTO)
                    }
                }
            };
        case AmendmentDraftChangeType.ServiceAddition:
            {
                const service = serviceModel.fromDTO(dto.value.service);
                return service ? makeServiceAddition(dto.value.serviceId, service) : undefined;
            }
        case AmendmentDraftChangeType.ServiceBundleRemoval:
        case AmendmentDraftChangeType.ServiceNameUpdate:
        case AmendmentDraftChangeType.ServiceDescriptionUpdate:
        case AmendmentDraftChangeType.ServiceTermsUpdate:
        case AmendmentDraftChangeType.ServiceTermsRemoval:
        case AmendmentDraftChangeType.ServiceTermination:
        case AmendmentDraftChangeType.ServiceCancellation:
        case AmendmentDraftChangeType.ServiceIntegratedClassUpdate:
        case AmendmentDraftChangeType.ServiceTaxabilityUpdate:
        case AmendmentDraftChangeType.PaymentApprovalRequirementUpdate:
        case AmendmentDraftChangeType.TaxSettingsUpdate:
        case AmendmentDraftChangeType.CreditCardFeeCoverageUpdate:
        case AmendmentDraftChangeType.LegalTermsDocumentsUpdate:
        case AmendmentDraftChangeType.AmendmentsApprovalMethodUpdate:
        case AmendmentDraftChangeType.InvoicesApprovalMethodUpdate:
        case AmendmentDraftChangeType.CustomPageAddition:
        case AmendmentDraftChangeType.CustomPageUpdate:
            return dto;
        default:
            return dropUnknownDraftChange(dto);
    }
}
